import { useEffect, useState } from 'react';
import { api } from '../api';
import bookingLinks from '../bookingLinks';
import LiveHotels from './LiveHotels';
import HotelInsightCard from './HotelInsightCard';

// Single-destination hotel picker. Used once per stop on a multi-city trip
// so each city gets its own hotel selection. Picks call onPick(hotel) which
// the parent wires to either tb.pickHotel (primary stop) or
// tb.setStopHotel(stopIndex, hotel) (additional stops).
export default function StopHotelPicker({
  destination,
  nights,
  checkin,
  checkout,
  picked,
  onPick,
  travelersLabel,
  scaleForTravelers,
  collapsedByDefault = false,
}) {
  const [hotels, setHotels] = useState(null);
  const [open, setOpen] = useState(!collapsedByDefault);

  useEffect(() => {
    if (!destination?.id || !open) return;
    api.getDestination(destination.id)
      .then(d => {
        const all = d.hotel_options || [];
        // For bundled sub-cities ("Lisbon" inside "Lisbon + Porto"), filter
        // by neighborhood matching the sub-city name so each stop only sees
        // its own city's hotels.
        if (destination._bundleParent || destination._bundleSubName) {
          const cityName = destination._bundleSubName || destination.city;
          const filtered = all.filter(h => (h.neighborhood || '').toLowerCase().includes(cityName.toLowerCase()));
          setHotels(filtered.length > 0 ? filtered : all);
        } else {
          // When this picker is the PRIMARY stop and the destination is itself
          // bundled, scope to the first sub-city's hotels too.
          const parts = (destination.city || '').split(' + ');
          if (parts.length > 1) {
            const cityName = parts[0].trim();
            const filtered = all.filter(h => (h.neighborhood || '').toLowerCase().includes(cityName.toLowerCase()));
            setHotels(filtered.length > 0 ? filtered : all);
          } else {
            setHotels(all);
          }
        }
      })
      .catch(() => setHotels([]));
  }, [destination?.id, destination?._bundleParent, destination?._bundleSubName, destination?.city, open]);

  const live = hotels === null;
  return (
    <div className="bg-navy-light rounded-2xl border border-brand-ink/10 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full p-3.5 flex items-center justify-between gap-2 text-left hover:bg-brand-ink/5 transition"
      >
        <div className="min-w-0 flex items-center gap-2.5">
          <span className="font-bold text-brand-ink truncate">{destination.city}</span>
          <span className="text-xs text-brand-ink/55 shrink-0">· {nights} night{nights === 1 ? '' : 's'}</span>
        </div>
        <div className="text-xs shrink-0">
          {picked ? (
            <span className="text-teal font-medium">&#10003; {picked.hotel_name?.slice(0, 28)}{picked.hotel_name?.length > 28 ? '…' : ''}</span>
          ) : (
            <span className="text-brand-ink/55 italic">No hotel</span>
          )}
          <span className="ml-2 text-brand-ink/35">{open ? '▾' : '▸'}</span>
        </div>
      </button>

      {open && (
        <div className="px-3.5 pb-3.5 space-y-3 border-t border-brand-ink/10 pt-3">
          {/* Live award rates for this destination */}
          {destination.airport_codes?.[0] && (
            <LiveHotels
              destinationCode={destination.airport_codes[0]}
              checkin={checkin}
              checkout={checkout}
            />
          )}

          {/* Curated hotel options */}
          {live && <div className="text-xs text-brand-ink/55">Loading hotel options for {destination.city}…</div>}
          {!live && hotels.length === 0 && (
            <div className="text-xs text-brand-ink/55">
              No curated points hotels for {destination.city} yet. Use the live award rates above or search Hyatt/Marriott directly.
            </div>
          )}
          {!live && hotels.map(h => {
            const selected = picked?.id === h.id;
            return (
              <div
                key={h.id}
                className={`rounded-xl border p-3 transition-all ${
                  selected ? 'border-indigo-500 ring-2 ring-indigo-500/30 bg-indigo-500/5' : 'border-brand-ink/10 hover:border-brand-ink/20'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">{h.hotel_name}</div>
                    <div className="flex items-center gap-2 text-[11px] text-brand-ink/65 mt-0.5 flex-wrap">
                      <span>via {h.hotel_program}</span>
                      {h.neighborhood && <><span className="text-brand-ink/45">·</span><span>{h.neighborhood}</span></>}
                      {h.star_rating && <span className="text-gold">{'★'.repeat(Math.floor(parseFloat(h.star_rating)))}</span>}
                    </div>
                    {h.cash_price_per_night > 0 && scaleForTravelers && (
                      <div className="text-[10px] text-brand-ink/55 mt-1">
                        Cash: ${h.cash_price_per_night}/night
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-black">{h.total_points?.toLocaleString()}</div>
                    <div className="text-[10px] text-brand-ink/65">{h.source_program === 'chase_ur' ? 'Chase' : 'Amex'} pts</div>
                  </div>
                </div>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  <button
                    onClick={() => onPick(h)}
                    className={`flex-1 min-w-[100px] py-1.5 rounded-lg text-xs font-bold transition ${
                      selected ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-600 text-brand-cream hover:bg-indigo-700'
                    }`}
                  >
                    {selected ? 'Selected for ' + destination.city : 'Pick for ' + destination.city}
                  </button>
                  {bookingLinks[h.hotel_program] && (
                    <a
                      href={bookingLinks[h.hotel_program].search}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 text-center bg-teal/15 text-teal rounded-lg text-xs font-medium hover:bg-teal/25 transition"
                    >
                      Book ↗
                    </a>
                  )}
                </div>
                <HotelInsightCard
                  hotelProgram={h.hotel_program}
                  hotelName={h.hotel_name}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
